/**
 * DashboardTemplates (admin) — ontwerp per voorpagina-template de widget-indeling
 * (hergebruikt de widget-editor in permanente edit-modus), naam en icoon. Opslaan =
 * pushen: gebruikers zonder eigen aanpassingen renderen het template live.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, TextField, CircularProgress } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T, Icon } from '../adminSettingsCommon';
import WidgetBoard from '../../CockpitHome/widgets/WidgetBoard';
import { WIDGETS } from '../../CockpitHome/widgets/registry';

const API = `${BACKEND_URL}/Api/dashboard-templates`;
let seq = 0;
const genId = (t) => `${t}-adm-${Date.now().toString(36)}-${seq++}`;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const reconcile = (widgets) => (Array.isArray(widgets) ? widgets : [])
  .map((w) => {
    const meta = WIDGETS[w?.type];
    if (!meta) return null;
    const h = meta.allowedHeights.includes(w.h) ? w.h : meta.defaultH;
    return { i: w.i || genId(w.type), type: w.type, w: clamp(Number(w.w) || meta.defaultW, meta.minW, meta.maxW), h, settings: (w.settings && typeof w.settings === 'object') ? w.settings : {} };
  })
  .filter(Boolean);

export default function DashboardTemplates() {
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState([]);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authFetch(API);
      const d = r.ok ? await r.json() : { templates: [] };
      setTemplates(d.templates || []);
    } catch { toast.error('Kon templates niet laden'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const select = (t) => {
    setActiveKey(t.key); setName(t.name); setIcon(t.icon || 'dashboard');
    setDraft(reconcile(t.layout?.widgets || []));
  };

  const reorder = (next) => setDraft(next);
  const updateWidget = (i, patch) => setDraft((p) => p.map((w) => (w.i === i ? { ...w, ...patch } : w)));
  const removeWidget = (i) => setDraft((p) => p.filter((w) => w.i !== i));
  const addWidgetAt = (type, index) => {
    const meta = WIDGETS[type];
    if (!meta) return;
    setDraft((p) => {
      const next = [...p];
      next.splice(clamp(index ?? next.length, 0, next.length), 0, { i: genId(type), type, w: meta.defaultW, h: meta.defaultH, settings: { ...(meta.defaultSettings || {}) } });
      return next;
    });
  };

  const save = async () => {
    if (!activeKey) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/${activeKey}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, icon, layout: { version: 1, widgets: draft } }),
      });
      if (!r.ok) throw new Error();
      toast.success('Template opgeslagen & gepusht');
      await load();
    } catch { toast.error('Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={22} /></Box>;

  return (
    <Box>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.secondary, mb: 2 }}>
        Ontwerp de standaard-indeling per template. <strong>Opslaan = pushen</strong>: iedereen die dit template
        gebruikt en zelf niets heeft aangepast, krijgt de nieuwe indeling automatisch.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {templates.map((t) => (
          <Box key={t.key} onClick={() => select(t)} sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.85, borderRadius: '0.6rem', cursor: 'pointer',
            border: `1.5px solid ${activeKey === t.key ? T.tertiary : T.surfaceHigh}`,
            bgcolor: activeKey === t.key ? '#dbcdff55' : T.surface, color: activeKey === t.key ? T.tertiary : T.secondary,
            fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.82rem',
          }}>
            <Icon name={t.icon || 'dashboard'} size={17} /> {t.name}
          </Box>
        ))}
      </Box>

      {!activeKey ? (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline }}>
          Kies hierboven een template om te ontwerpen.
        </Typography>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', mb: 2.5 }}>
            <TextField label="Naam" size="small" value={name} onChange={(e) => setName(e.target.value)} sx={{ minWidth: 200 }} />
            <TextField label="Icoon (Material Symbol)" size="small" value={icon} onChange={(e) => setIcon(e.target.value)} sx={{ minWidth: 220 }}
              InputProps={{ endAdornment: <Icon name={icon || 'dashboard'} size={20} sx={{ color: T.tertiary }} /> }} />
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" disableElevation onClick={save} disabled={saving}
              startIcon={<Icon name="publish" size={17} />}
              sx={{ fontFamily: T.fontBody, textTransform: 'none', fontWeight: 700, bgcolor: T.tertiary, '&:hover': { bgcolor: '#574d76' } }}>
              {saving ? 'Opslaan…' : 'Opslaan & pushen'}
            </Button>
          </Box>

          <Box sx={{ bgcolor: '#f8f9fa', borderRadius: '0.9rem', p: 2, border: `1px solid ${T.surfaceHigh}` }}>
            <WidgetBoard
              widgets={draft}
              editMode
              reorder={reorder}
              updateWidget={updateWidget}
              removeWidget={removeWidget}
              addWidgetAt={addWidgetAt}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
